import type { ReactNode } from "react";
import { useRouteContext } from "@tanstack/react-router";

import { useCertifications, useDashboardSystemMessages } from "@/queries";
import {
  parseCertificationMonth,
  normalizeStatus,
} from "@/utils/certifications";
import { renderRichText } from "@/components/system-messages/RichText";
import type { Certification } from "@/models/file-submission";

export type MessageType =
  | "warning"
  | "info"
  | "error"
  | "certification"
  | "maintenance"
  | "update"
  | "urgent";

export interface Message {
  id: string;
  type: MessageType;
  title: string;
  body: ReactNode;
}

function mapLabelCodeToType(labelCode: string): MessageType {
  switch (labelCode) {
    case "Certification":
      return "certification";
    case "Maintenance":
      return "maintenance";
    case "CAPSDAC Update":
      return "update";
    case "Action Required":
    case "Urgent":
      return "urgent";
    default:
      return "info";
  }
}

function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function toLocalMidnight(iso: string): Date {
  const [year, month, day] = iso.split("T")[0].split("-").map(Number);
  const d = new Date(year, month - 1, day);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatReadableDate(d: Date): string {
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function buildCertificationReminder(
  c: Certification,
  today: Date
): Message | null {
  const calId = String(c.ReportingCalendarId ?? "");

  if (!calId.startsWith("MNTH")) return null;

  const status = normalizeStatus(c.Status);
  if (status !== "Uncertified" && status !== "Decertified") return null;

  const deadline = c.Deadline ? toLocalMidnight(c.Deadline) : null;
  const amendmentEnd = c.AmendmentEnd ? toLocalMidnight(c.AmendmentEnd) : null;
  if (!deadline && !amendmentEnd) return null;

  const t = startOfDay(today).getTime();
  const name = parseCertificationMonth(calId, c.WindowStart);

  if (
    deadline &&
    t >= addDays(deadline, -7).getTime() &&
    t <= addDays(deadline, -1).getTime()
  ) {
    return {
      id: `cert-approaching-${c.Id}`,
      type: "certification",
      title: "Certification deadline approaching",
      body: (
        <>
          Your <strong>{name}</strong> CAPSDAC monthly certification is due on{" "}
          <strong>{formatReadableDate(deadline)}</strong>. Please review and
          certify your data before the deadline.
        </>
      ),
    };
  }

  if (deadline && t === deadline.getTime()) {
    return {
      id: `cert-due-today-${c.Id}`,
      type: "certification",
      title: "Certification due today",
      body: (
        <>
          Your <strong>{name}</strong> CAPSDAC monthly certification is due
          today. Please certify your data by 11:59 p.m. PT.
        </>
      ),
    };
  }

  if (
    deadline &&
    amendmentEnd &&
    t > deadline.getTime() &&
    t <= addDays(amendmentEnd, -5).getTime()
  ) {
    return {
      id: `cert-past-due-${c.Id}`,
      type: "urgent",
      title: "Certification past due",
      body: (
        <>
          Your <strong>{name}</strong> CAPSDAC monthly certification is past
          due. Please certify your data as soon as possible.
        </>
      ),
    };
  }

  if (
    amendmentEnd &&
    t >= addDays(amendmentEnd, -4).getTime() &&
    t <= amendmentEnd.getTime()
  ) {
    return {
      id: `cert-final-${c.Id}`,
      type: "urgent",
      title: "Certification final reminder",
      body: (
        <>
          Your <strong>{name}</strong> CAPSDAC monthly certification has not
          been completed. The amendment window closes on{" "}
          <strong>{formatReadableDate(amendmentEnd)}</strong>, at 11:59 p.m. PT.
        </>
      ),
    };
  }

  return null;
}

function buildNonMonthlyDeadlineMessage(
  c: Certification,
  today: Date
): Message | null {
  const calId = String(c.ReportingCalendarId ?? "");
  if (calId.startsWith("MNTH")) return null;
  if (!c.Deadline) return null;
  if (c.Status === "Closed" || c.Status === "Certified") return null;

  const name = parseCertificationMonth(calId, c.WindowStart);
  const deadline = toLocalMidnight(c.Deadline);
  const daysLeft = Math.ceil(
    (deadline.getTime() - startOfDay(today).getTime()) / (1000 * 60 * 60 * 24)
  );

  if (daysLeft <= 0 || daysLeft > 14) return null;

  const deadlineFormatted = deadline.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });

  return {
    id: `deadline-${c.Id}`,
    type: "warning",
    title: `${name} deadline approaching`,
    body: `${daysLeft} day${daysLeft === 1 ? "" : "s"} left. Submit by ${deadlineFormatted}.`,
  };
}

export function buildCycleMessages(
  certifications: Certification[],
  today: Date
): Message[] {
  const day = startOfDay(today);
  const messages: Message[] = [];

  for (const c of certifications) {
    const message =
      buildCertificationReminder(c, day) ??
      buildNonMonthlyDeadlineMessage(c, day);
    if (message) messages.push(message);
  }

  return messages;
}

export function useDashboardMessages(leaid: string): Message[] {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const canViewCertifications = !!leaContext.hasRole(
    "ReportsEOY",
    "ReportsFall",
    "ReportsMonthly"
  );
  const { data: certifications } = useCertifications(
    leaid,
    canViewCertifications
  );
  const { data: systemMessages } = useDashboardSystemMessages(leaid);

  const today = startOfDay(new Date());

  const adminMessages: Message[] = (systemMessages ?? []).map((m, idx) => ({
    id: `sys-${idx}-${m.title}`,
    type: mapLabelCodeToType(m.labelCode),
    title: m.title,
    body: renderRichText(m.messageBody),
  }));

  const cycleMessages = buildCycleMessages(certifications ?? [], today);

  return [...adminMessages, ...cycleMessages];
}
